import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Shield, 
  CheckCircle, 
  Award, 
  AlertTriangle,
  Building2,
  FileCheck,
  ArrowRight,
  BadgeCheck,
  ShieldCheck,
  KeyRound,
  Settings,
  HardDrive,
  MapPin
} from "lucide-react";

const CyberEssentialsUK = () => {
  // UK local areas for SEO
  const ukAreas = [
    "London", "Manchester", "Birmingham", "Leeds", "Glasgow",
    "Liverpool", "Newcastle", "Sheffield", "Bristol", "Edinburgh",
    "Cardiff", "Belfast", "Nottingham", "Southampton", "Leicester"
  ];

  // FAQ items
  const faqItems = [
    {
      question: "What is Cyber Essentials certification?",
      answer: "Cyber Essentials is a UK government-backed scheme that helps organisations protect themselves against common cyber attacks. It provides a clear statement of the basic controls organisations should have in place to protect themselves."
    },
    {
      question: "Is Cyber Essentials mandatory in the UK?",
      answer: "While not mandatory for all businesses, Cyber Essentials is required for organisations bidding for UK government contracts involving handling sensitive and personal information. Many private sector organisations also require suppliers to hold this certification."
    },
    {
      question: "How long does Cyber Essentials certification take?",
      answer: "The basic Cyber Essentials certification typically takes 1-2 weeks from start to finish, depending on your current security posture. Our Micro Package is designed for quick certification for small businesses."
    },
    {
      question: "What's the difference between Cyber Essentials and Cyber Essentials Plus?",
      answer: "Cyber Essentials is a self-assessment questionnaire verified by an assessor. Cyber Essentials Plus includes all of that plus hands-on technical verification where an assessor tests your systems directly to verify the controls are in place."
    },
    {
      question: "How much does Cyber Essentials cost?",
      answer: "Our Micro Package for businesses with 1-9 employees starts at £495 plus the £384 IASME certification fee. This includes full support throughout the certification process."
    },
    {
      question: "How long is Cyber Essentials valid?",
      answer: "Cyber Essentials certification is valid for 12 months. Annual recertification is required to maintain your certified status and demonstrate ongoing commitment to cyber security."
    }
  ];

  // Schema markup for SEO
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": "https://www.4fox.uk/cyber-essentials-uk#service",
        "name": "Cyber Essentials Certification UK",
        "description": "Government-backed cyber security certification for UK businesses. Protect against common cyber attacks and demonstrate your security commitment.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "4Fox",
          "url": "https://www.4fox.uk"
        },
        "areaServed": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "serviceType": "Cyber Security Certification"
      },
      {
        "@type": "FAQPage",
        "@id": "https://www.4fox.uk/cyber-essentials-uk#faq",
        "mainEntity": faqItems.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      },
    ]
  };

  const controls = [
    {
      icon: Shield,
      title: "Firewalls",
      description: "Secure your internet connection with properly configured firewalls to prevent unauthorised access."
    },
    {
      icon: Settings,
      title: "Secure Configuration",
      description: "Configure computers and network devices securely to reduce vulnerabilities."
    },
    {
      icon: KeyRound,
      title: "Access Control",
      description: "Control who has access to your data and services with proper user account management."
    },
    {
      icon: AlertTriangle,
      title: "Malware Protection",
      description: "Protect against viruses and other malware with appropriate security software."
    },
    {
      icon: HardDrive,
      title: "Security Updates",
      description: "Keep devices and software up to date with the latest security patches."
    }
  ];

  const benefits = [
    {
      icon: Award,
      title: "Government Contract Eligibility",
      description: "Required for many UK government contracts handling sensitive data"
    },
    {
      icon: Shield,
      title: "Protection Against 80% of Attacks",
      description: "Guards against the most common cyber threats targeting UK businesses"
    },
    {
      icon: Building2,
      title: "Competitive Advantage",
      description: "Stand out from competitors and win more business with certified security"
    },
    {
      icon: FileCheck,
      title: "Insurance Benefits",
      description: "Many insurers offer better rates for Cyber Essentials certified businesses"
    }
  ];

  return (
    <>
      <SEO 
        title="Cyber Essentials Certification UK | Government-Backed Security"
        description="Get Cyber Essentials certified. UK government-backed cyber security certification. Protect your business, win contracts, and demonstrate security commitment."
        keywords={[
          "cyber essentials UK",
          "cyber essentials certification",
          "cyber essentials scheme",
          "UK cyber security certification",
          "government cyber security",
          "NCSC cyber essentials",
          "cyber essentials requirements",
          "cyber essentials cost UK"
        ]}
        canonical="https://www.4fox.uk/cyber-essentials-uk"
      />
      
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="bg-primary pt-32 pb-16 md:pb-20">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center">
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-2 mb-6">
                <BadgeCheck className="w-5 h-5 text-accent" />
                <span className="text-white/90 text-sm font-medium">
                  IASME Accredited Certification Body
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Cyber Essentials Certification UK
              </h1>
              
              <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Protect your business with the UK government-backed cyber security certification. 
                Essential for winning contracts and safeguarding against common attacks.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-accent hover:bg-accent/90 text-white font-semibold text-lg px-8"
                >
                  <Link to="/services/cyber-essentials-micro">
                    Get Certified - From £495
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline" 
                  size="lg"
                  className="border-white/50 text-white bg-transparent hover:bg-white/10 font-semibold"
                >
                  <Link to="/contact">
                    Speak to an Expert
                  </Link>
                </Button>
              </div>
              
              {/* Trust Signals */}
              <div className="flex flex-wrap justify-center gap-6 mt-10 text-white/70 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>NCSC Approved Scheme</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>Quick Certification Process</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <span>Full Support Included</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb Navigation */}
        <div className="container mx-auto px-4 sm:px-6 pt-8">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Services", href: "/services" },
              { label: "Cyber Essentials UK" }
            ]}
          />
        </div>

        {/* What is Cyber Essentials Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  What is Cyber Essentials?
                </h2>
                <p className="text-muted-foreground text-lg">
                  Understanding the UK's essential cyber security certification
                </p>
              </div>
              
              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p className="mb-6">
                  <strong className="text-foreground">Cyber Essentials</strong> is a UK government-backed scheme 
                  developed by the National Cyber Security Centre (NCSC). It provides a framework for organisations 
                  to implement fundamental security controls that protect against the most common cyber attacks.
                </p>
                <p className="mb-6">
                  The scheme focuses on five key technical controls that, when properly implemented, can prevent 
                  around 80% of cyber attacks. For UK businesses, especially those working with government or 
                  handling sensitive data, Cyber Essentials certification has become essential.
                </p>
                <p>
                  Whether you're a small business or a large enterprise, Cyber Essentials provides a clear, 
                  achievable security baseline that demonstrates your commitment to protecting customer data 
                  and business operations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-12 bg-primary border-t-4 border-accent">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                Ready to protect your business?
              </h3>
              <p className="text-white/80 mb-6">
                Our Micro Package is perfect for UK businesses with 1-9 employees. 
                Get certified quickly with expert support throughout.
              </p>
              <Button 
                asChild 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-white font-semibold"
              >
                <Link to="/services/cyber-essentials-micro">
                  View Micro Package - From £495
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* 5 Controls Section */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                The 5 Cyber Essentials Controls
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                These five security controls form the foundation of the Cyber Essentials scheme
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {controls.map((control, index) => (
                <Card 
                  key={index} 
                  className="group bg-card border-2 border-border hover:border-accent transition-all duration-300"
                >
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mb-3 group-hover:bg-accent transition-colors duration-300">
                      <control.icon className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-300" />
                    </div>
                    <CardTitle className="text-lg">{control.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground">
                      {control.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Why UK Businesses Need Cyber Essentials
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Certification delivers real benefits for your business
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="group flex gap-4 p-6 bg-card rounded-lg border-2 border-border hover:border-accent transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-accent transition-colors duration-300">
                    <benefit.icon className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CE vs CE Plus Section */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Cyber Essentials vs Cyber Essentials Plus
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Understanding the two levels of certification
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Cyber Essentials */}
              <Card className="border-2 border-primary">
                <CardHeader className="bg-primary text-white rounded-t-lg">
                  <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8" />
                    <div>
                      <CardTitle className="text-xl">Cyber Essentials</CardTitle>
                      <CardDescription className="text-white/80">
                        Self-assessment certification
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Complete online questionnaire</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Verified by accredited assessor</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Quick certification process</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Certificate and badge provided</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Suitable for most requirements</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              {/* Cyber Essentials Plus */}
              <Card className="border-2 border-accent">
                <CardHeader className="bg-accent text-white rounded-t-lg">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-8 h-8" />
                    <div>
                      <CardTitle className="text-xl">Cyber Essentials Plus</CardTitle>
                      <CardDescription className="text-white/80">
                        Includes technical verification
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Everything in Cyber Essentials</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Hands-on technical testing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Vulnerability scanning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Higher assurance level</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Required for some contracts</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            
            <div className="text-center mt-8">
              <p className="text-muted-foreground mb-4">
                Not sure which level you need? Our team can advise based on your requirements.
              </p>
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                <Link to="/contact">
                  Get Expert Advice
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-muted-foreground">
                  Common questions about Cyber Essentials certification in the UK
                </p>
              </div>
              
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-border">
                    <AccordionTrigger className="text-left hover:text-accent">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* UK Service Areas */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Cyber Essentials Across the UK
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We support businesses throughout the United Kingdom with Cyber Essentials certification
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
              {ukAreas.map((area, index) => (
                <div 
                  key={index} 
                  className="group flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2 hover:border-accent transition-all duration-300"
                >
                  <div className="w-6 h-6 bg-muted rounded flex items-center justify-center group-hover:bg-accent transition-colors duration-300">
                    <MapPin className="w-3 h-3 text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-sm font-medium text-foreground">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 md:py-20 bg-primary">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <ShieldCheck className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Start Your Cyber Essentials Journey Today
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
                Protect your business, win more contracts, and demonstrate your commitment 
                to cyber security with Cyber Essentials certification.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-accent hover:bg-accent/90 text-white font-semibold text-lg px-8"
                >
                  <Link to="/services/cyber-essentials-micro">
                    Get Started - From £495
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline" 
                  size="lg"
                  className="border-white/50 text-white bg-transparent hover:bg-white/10 font-semibold"
                >
                  <Link to="/contact">
                    Request a Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default CyberEssentialsUK;
